import {
    Fragment,
    FunctionComponent,
    useEffect,
    useState,
    useCallback,
} from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    GetVouchersSearchParams,
    useGetVouchersQuery,
    VouchersOverviewUI,
    Voucher,
} from '@starjack/entities/voucher';
import { useNotification } from '@starjack/features/notifications';
import { fetchAuthWrapper } from '@starjack/api';
import { urlBuilder } from '@starjack/utils';
import { useDebouncedEffect } from '@starjack/utils';
import { getSerializedObjectForQuery } from '@starjack/utils';
import { Theme } from '@mui/material/styles';

const downloadContentGeneric = (filename: string, content: string) => {
    const element = document.createElement('a');
    element.setAttribute('href', content);
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
};

const voucherQuery = ['code'];

// Extended theme interface for widgets that expect app-specific theme properties
interface ExtendedTheme extends Theme {
    page: {
        verticalSpacing: number;
        backgroundColor: string;
        template: {
            base: {
                paddingX: number;
                paddingY: number;
            };
        };
    };
}

export const VouchersContent: FunctionComponent = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { addNotification } = useNotification();
    const theme = useTheme() as ExtendedTheme;

    const code = searchParams.get('code') || '';
    const page = parseInt(searchParams.get('page') || '0', 10);
    const size = parseInt(searchParams.get('size') || '10', 10);

    const [localCode, setLocalCode] = useState(code);
    const [voucherSearch, setVoucherSearch] = useState<GetVouchersSearchParams>(
        {
            code: code || undefined,
            page,
            size,
        }
    );

    const { data: voucherResult, isFetching: isLoading } =
        useGetVouchersQuery(voucherSearch);

    const downloadVoucher = useCallback(
        async (voucherId: string): Promise<string | null> => {
            const response = await fetchAuthWrapper(
                `${urlBuilder('order', 'vouchers')}/${voucherId}/download`,
                {
                    headers: {
                        Accept: '*',
                    },
                    method: 'GET',
                }
            );

            if (!response.ok) {
                try {
                    const errorData = await response.json();
                    const errorCode = errorData?.errors?.[0]?.error;

                    if (errorCode === 'voucher_not_paid') {
                        addNotification({
                            type: 'error',
                            message:
                                'Der Gutschein wurde noch nicht bezahlt und kann daher nicht heruntergeladen werden.',
                        });
                    } else {
                        addNotification({
                            type: 'error',
                            message:
                                'Fehler beim Download. Bitte später noch einmal versuchen.',
                        });
                    }
                } catch {
                    addNotification({
                        type: 'error',
                        message:
                            'Fehler beim Download. Bitte später noch einmal versuchen.',
                    });
                }
                return null;
            }

            return URL.createObjectURL(await response.blob());
        },
        [addNotification]
    );

    const onDownload = useCallback(
        async (voucher: Voucher) => {
            const blobUrl = await downloadVoucher(voucher.id);

            if (blobUrl) {
                downloadContentGeneric(
                    `Gutschein_${voucher.code || voucher.id}.pdf`,
                    blobUrl
                );
            }
        },
        [downloadVoucher]
    );

    useDebouncedEffect(
        () => {
            setVoucherSearch((prevState) => {
                if (localCode) {
                    return {
                        ...prevState,
                        code: localCode,
                        page: 0,
                    };
                } else {
                    const nextState = { ...prevState };
                    delete nextState.code;
                    nextState.page = 0;
                    return nextState;
                }
            });
        },
        450,
        [localCode]
    );

    useEffect(() => {
        const searchParams = new URLSearchParams(
            getSerializedObjectForQuery(voucherSearch, voucherQuery)
        );
        navigate(`/vouchers?${searchParams.toString()}`, { replace: true });
    }, [voucherSearch, navigate]);

    return (
        <Fragment>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                {t('vouchersPage.title')}
            </Typography>
            <Stack
                alignItems='flex-start'
                spacing={theme.page.verticalSpacing ?? 4}
            >
                <Card
                    sx={{
                        width: '100%',
                    }}
                >
                    <CardContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Grid spacing={3} container>
                            <Grid xs={12} item>
                                <TextField
                                    value={localCode}
                                    label={t('vouchersPage.code')}
                                    onChange={(e) => {
                                        const { value } = e.target;

                                        setLocalCode(value);
                                    }}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                mt: 3,
                            }}
                        >
                            {!!(
                                voucherSearch.code ||
                                voucherSearch.page ||
                                voucherSearch.size
                            ) && (
                                <LoadingButton
                                    onClick={() => {
                                        setLocalCode('');
                                        setVoucherSearch({
                                            page: 0,
                                            size: 10,
                                        });
                                    }}
                                    sx={{
                                        color: 'text.secondary',
                                        ml: 'auto',
                                        textTransform: 'capitalize',
                                    }}
                                    loading={false}
                                >
                                    {t('vouchersPage.reset')}
                                </LoadingButton>
                            )}
                        </Box>
                    </CardContent>
                </Card>

                <VouchersOverviewUI
                    currentPageVouchers={voucherResult?.content || []}
                    vouchersTotal={voucherResult?.totalElements || 0}
                    vouchersPerPage={voucherSearch.size || 25}
                    onPageSizeChange={(nextValue) => {
                        setVoucherSearch((prevState) => ({
                            ...prevState,
                            size: nextValue,
                        }));
                    }}
                    onPageChange={(nextValue) => {
                        setVoucherSearch((prevState) => ({
                            ...prevState,
                            page: nextValue,
                        }));
                    }}
                    vouchersLoading={isLoading}
                    locale={i18n.language}
                    translations={{
                        de: i18n.store.data.de.translation as Record<
                            string,
                            string
                        >,
                        en: i18n.store.data.en.translation as Record<
                            string,
                            string
                        >,
                    }}
                    onDownload={onDownload}
                />
            </Stack>
        </Fragment>
    );
};
